// src/HomePage.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  // ==============================================
  // ALL STYLES – INLINE (NO EXTERNAL CSS)
  // ==============================================
  const style = {
    container: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      backgroundColor: '#f8f9fa',
    },

    // NAVBAR
    navbar: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '1rem 10%',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    },
    navContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    logo: {
      fontSize: '1.5rem',
      fontWeight: 700,
      textDecoration: 'none',
      color: 'white',
    },
    navLinks: {
      display: 'flex',
      listStyle: 'none',
      gap: '2rem',
      margin: 0,
      padding: 0,
    },
    navLink: {
      color: 'white',
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.2s',
    },
    activeLink: {
      color: '#f0f0f0',
      fontWeight: 'bold',
    },

    // HERO
    hero: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '80px 10%',
      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      color: 'white',
      flexWrap: 'wrap',
      gap: '40px',
    },
    heroContent: {
      flex: 1,
      minWidth: '300px',
    },
    heroTitle: {
      fontSize: '2.8rem',
      fontWeight: 800,
      margin: '0 0 16px',
      letterSpacing: '-1px',
    },
    tagline: {
      fontSize: '1.3rem',
      marginBottom: '24px',
      opacity: 0.95,
    },
    heroButtons: {
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap',
    },
    btnPrimary: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 600,
      textDecoration: 'none',
      display: 'inline-block',
      transition: 'all 0.3s',
      backgroundColor: 'white',
      color: '#007bff',
    },
    btnSecondary: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 600,
      textDecoration: 'none',
      display: 'inline-block',
      transition: 'all 0.3s',
      backgroundColor: 'transparent',
      color: 'white',
      border: '2px solid white',
    },
    heroImage: {
      flex: 1,
      minWidth: '300px',
      textAlign: 'center',
    },
    placeholderImage: {
      width: '280px',
      height: '280px',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2.5rem',
      margin: '0 auto',
      backdropFilter: 'blur(5px)',
      fontWeight: 'bold',
      color: 'white',
    },

    // SECTIONS
    section: {
      padding: '80px 10%',
      textAlign: 'center',
    },
    sectionAlt: {
      backgroundColor: '#fff',
      padding: '80px 10%',
    },
    sectionContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: '2.2rem',
      marginBottom: '24px',
      color: '#007bff',
    },
    sectionText: {
      fontSize: '1.1rem',
      maxWidth: '800px',
      margin: '0 auto 16px',
      color: '#555',
    },

    // GRID & CARDS
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '30px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    card: {
      background: 'white',
      padding: '32px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      textAlign: 'left',
      transition: 'transform 0.3s',
    },
    cardTitle: {
      color: '#007bff',
      marginBottom: '16px',
      fontSize: '1.5rem',
    },

    // AMBITIONS
    ambitions: {
      listStyle: 'none',
      padding: 0,
      maxWidth: '700px',
      margin: '0 auto',
      textAlign: 'left',
    },
    ambitionsItem: {
      padding: '12px 0',
      fontSize: '1.1rem',
      borderBottom: '1px dashed #ddd',
    },

    // CTA
    cta: {
      background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
      color: 'white',
      padding: '80px 10%',
      textAlign: 'center',
    },
    ctaTitle: {
      fontSize: '2.2rem',
      marginBottom: '16px',
    },
    ctaText: {
      fontSize: '1.1rem',
      marginBottom: '24px',
      opacity: 0.9,
    },
    btnPrimaryLarge: {
      padding: '16px 32px',
      fontSize: '1.2rem',
      backgroundColor: 'white',
      color: '#28a745',
      borderRadius: '50px',
      fontWeight: 700,
      textDecoration: 'none',
      display: 'inline-block',
      transition: 'all 0.3s',
    },

    // FOOTER
    footer: {
      backgroundColor: '#343a40',
      color: '#adb5bd',
      textAlign: 'center',
      padding: '40px 10%',
      fontSize: '0.9rem',
    },
    footerLink: {
      color: '#adb5bd',
      textDecoration: 'none',
    },

    // RESPONSIVE
    mobile: {
      hero: {
        flexDirection: 'column',
        textAlign: 'center',
        padding: '60px 5%',
      },
      heroTitle: { fontSize: '2.2rem' },
      placeholderImage: { width: '200px', height: '200px', fontSize: '1.8rem' },
      section: { padding: '60px 5%' },
      sectionAlt: { padding: '60px 5%' },
      cta: { padding: '60px 5%' },
      navbar: { padding: '1rem 5%' },
      navContainer: { flexDirection: 'column', gap: '1rem' },
      navLinks: { gap: '1rem' },
    },
  };

  // Responsive helper
  const r = (base, mobile) => (window.innerWidth <= 768 ? { ...base, ...mobile } : base);

  return (
    <div style={style.container}>
      {/* NAVBAR */}
      <nav style={r(style.navbar, style.mobile.navbar)}>
        <div style={r(style.navContainer, style.mobile.navContainer)}>
          <Link to="/" style={style.logo}>Kigaa Self-Help Water Society</Link>
          <ul style={r(style.navLinks, style.mobile.navLinks)}>
            <li><Link to="/" style={{ ...style.navLink, ...style.activeLink }}>Home</Link></li>
            <li><Link to="/board" style={style.navLink}>Board of Founders</Link></li>
            <li><Link to="/employees" style={style.navLink}>Who We Are</Link></li>
            <li><Link to="/gallery" style={style.navLink}>Gallery</Link></li>
            <li><Link to="/dashboard" style={style.navLink}>Admin Dashboard</Link></li>
          </ul>
        </div>
      </nav>

      {/* HERO */}
      <section style={r(style.hero, style.mobile.hero)}>
        <div style={style.heroContent}>
          <h1 style={r(style.heroTitle, style.mobile.heroTitle)}>Kigaa Self-Help Water Society</h1>
          <p style={style.tagline}>Transparent. Efficient. Community-Driven Water Management.</p>
          <div style={style.heroButtons}>
            <Link to="/dashboard" style={style.btnPrimary}>Access Dashboard</Link>
            <a href="#about" style={style.btnSecondary}>Learn More</a>
          </div>
        </div>
        <div style={style.heroImage}>
          <div style={r(style.placeholderImage, style.mobile.placeholderImage)}>
            MAJI KWA WOTE
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" style={r(style.section, style.mobile.section)}>
        <div style={style.sectionContent}>
          <h2 style={style.sectionTitle}>About Us</h2>
          <p style={style.sectionText}>
            We are a community-focused water utility provider committed to delivering <strong>clean, reliable, and fairly billed water</strong> to every household in our service area.
          </p>
          <p style={style.sectionText}>
            Using modern technology and transparent billing systems, we eliminate confusion, reduce disputes, and ensure every customer pays only for what they use.
          </p>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section style={r(style.sectionAlt, style.mobile.sectionAlt)}>
        <div style={style.grid}>
          <div style={style.card}>
            <h3 style={style.cardTitle}>Our Mission</h3>
            <p style={style.sectionText}>
              To provide <strong>affordable, accurate, and accessible</strong> water billing services that empower residents and promote sustainable water use.
            </p>
          </div>
          <div style={style.card}>
            <h3 style={style.cardTitle}>Our Vision</h3>
            <p style={style.sectionText}>
              A community where <strong>every drop is accounted for</strong>, every bill is fair, and every resident trusts their water provider.
            </p>
          </div>
        </div>
      </section>

      {/* AMBITIONS */}
      <section style={r(style.section, style.mobile.section)}>
        <div style={style.sectionContent}>
          <h2 style={style.sectionTitle}>Our Ambitions</h2>
          <ul style={style.ambitions}>
            <li style={style.ambitionsItem}>Expand smart metering to <strong>100% of households</strong> by 2027</li>
            <li style={style.ambitionsItem}>Reduce billing disputes by <strong>95%</strong> through automation</li>
            <li style={style.ambitionsItem}>Launch a <strong>mobile app</strong> for real-time usage & payments</li>
            <li style={style.ambitionsItem}>Partner with local schools for <strong>water conservation education</strong></li>
            <li style={style.ambitionsItem}>Achieve <strong>zero manual errors</strong> in monthly billing cycles</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section style={r(style.cta, style.mobile.cta)}>
        <h2 style={style.ctaTitle}>Ready to Manage Your Account?</h2>
        <p style={style.ctaText}>Admins can access the full billing dashboard with real-time data.</p>
        <Link to="/dashboard" style={style.btnPrimaryLarge}>Go to Dashboard</Link>
      </section>

      {/* FOOTER */}
      <footer style={style.footer}>
        <p>&copy; {new Date().getFullYear()} Kigaa Self-Help Water Society. All rights reserved.</p>
        <p>
          <a href="/privacy" style={style.footerLink}>Privacy Policy</a> •{' '}
          <a href="/terms" style={style.footerLink}>Terms of Service</a>
        </p>
      </footer>
    </div>
  );
};

export default HomePage;